/**
 * Status badges and row actions at the top of the User Details Profile tab.
 * Extracted from UserDetailsModal to keep that file under 500 lines.
 */

import type { UserProfile } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Building2, Check, Power, Shield, ShieldCheck, X } from 'lucide-react';

interface UserDetailsStatusBarProps {
  selectedUser: UserProfile;
  /** True when the logged-in user is a Django superuser (may use Super admin switch). */
  isSuperAdmin: boolean;
  actionLoading: boolean;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleActive: (userProfile: UserProfile) => void;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  /** Platform staff only: open the change-company (relink) dialog. */
  openChangeCompanyDialog?: (userProfile: UserProfile) => void;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => void;
}

export function UserDetailsStatusBar({
  selectedUser,
  isSuperAdmin,
  actionLoading,
  isSelf,
  handleToggleActive,
  openChangeRoleDialog,
  openChangeCompanyDialog,
  handleToggleAdmin,
}: UserDetailsStatusBarProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
      <div className="flex items-center gap-3">
        {/* CR-4551 round 4: surface the "Invite Sent" intermediate
            state instead of mis-labelling unaccepted invitees as
            Active in the User Details modal. */}
        {!selectedUser.user.is_active ? (
          <Badge variant="secondary">
            <X className="h-3 w-3 mr-1" />
            Inactive
          </Badge>
        ) : selectedUser.user.invitation_sent && !selectedUser.user.has_joined ? (
          <Badge variant="outline" className="text-amber-600 border-amber-600">
            Invite Sent
          </Badge>
        ) : (
          <Badge variant="default" className="bg-green-500">
            <Check className="h-3 w-3 mr-1" />
            Active
          </Badge>
        )}
        {!selectedUser.is_approved && (
          <Badge variant="outline" className="text-amber-600 border-amber-600">
            Pending Approval
          </Badge>
        )}
        {selectedUser.role_details?.is_admin_role && (
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Admin role
          </Badge>
        )}
        {selectedUser.user.is_superuser && (
          <Badge variant="outline" className="text-purple-600 border-purple-600">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Super admin
          </Badge>
        )}
      </div>
      <div className="flex gap-2 items-center">
        {!isSelf(selectedUser) && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleActive(selectedUser)}
              disabled={actionLoading}
            >
              <Power className="h-4 w-4 mr-1" />
              {selectedUser.user.is_active ? 'Disable' : 'Enable'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openChangeRoleDialog(selectedUser)}
            >
              <Shield className="h-4 w-4 mr-1" />
              Change Role
            </Button>
            {openChangeCompanyDialog && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openChangeCompanyDialog(selectedUser)}
                disabled={actionLoading}
              >
                <Building2 className="h-4 w-4 mr-1" />
                Change Company
              </Button>
            )}
            {/* CR-5367: Super Admin toggle is only ever applicable
                to Valuechain (internal Helix) users — Customer and
                Supplier user details must not show this control,
                and the caller must already hold Super Admin rights
                to grant/revoke. The two gates compose so that a
                Valuechain super admin opening a Customer/Supplier
                user detail will not see the toggle either. */}
            {isSuperAdmin && selectedUser.company_type === 'helix' && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                <label htmlFor="user-super-admin-toggle" className="text-sm text-muted-foreground">
                  Super admin
                </label>
                <Switch
                  id="user-super-admin-toggle"
                  checked={selectedUser.user.is_superuser || false}
                  onCheckedChange={(checked) => handleToggleAdmin(selectedUser, checked)}
                  disabled={actionLoading}
                  aria-label="Grant or revoke Django superuser and staff"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UserDetailsStatusBar;
